import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Flag, ShieldAlert, Clock, CheckCircle2, XCircle, ExternalLink, Loader2 } from "lucide-react";
import { REPORT_CATEGORIES } from "@/components/ReportModal";
import { Link } from "react-router-dom";

const STATUS_TABS = [
  { key: "open", label: "Open", color: "text-rose-300" },
  { key: "resolved", label: "Resolved", color: "text-emerald-300" },
  { key: "rejected", label: "Rejected", color: "text-white/40" },
];

const CATEGORY_LABEL = Object.fromEntries(REPORT_CATEGORIES.map((c) => [c.id, c.label]));

/** Compliance Roadmap Item 16 — Admin moderation queue.
 *
 *  Lists user-submitted reports. Admin can mark a report resolved (after
 *  taking action against the offending content) or rejected (bad-faith /
 *  no violation), with an optional internal note. */
export default function AdminReports() {
  const qc = useQueryClient();
  const [status, setStatus] = useState("open");
  const { data, isLoading } = useQuery({
    queryKey: ["admin-reports", status],
    queryFn: async () => (await api.get(`/admin/reports?status=${status}&limit=100`)).data,
    refetchInterval: 15000,
  });

  const patch = useMutation({
    mutationFn: async ({ id, status: newStatus, admin_note }) =>
      (await api.patch(`/admin/reports/${id}`, { status: newStatus, admin_note: admin_note || null })).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin-reports"] });
      toast.success("Report updated.");
    },
    onError: (err) => toast.error(apiError(err) || "Update failed."),
  });

  const counts = data?.counts || {};
  const items = data?.items || [];

  return (
    <div data-testid="admin-reports">
      <div className="flex items-center gap-2 mb-1">
        <Flag className="h-5 w-5 text-rose-400" />
        <h1 className="font-display text-2xl text-white tracking-[0.08em]">CONTENT REPORTS</h1>
      </div>
      <p className="text-sm text-white/50 mb-6">User-submitted reports of prohibited or infringing content. Categories match the <Link to="/legal/ugc-rules" className="text-cyan-300 hover:text-cyan-200 underline underline-offset-2">UGC Rules</Link>.</p>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            data-testid={`admin-reports-tab-${t.key}`}
            onClick={() => setStatus(t.key)}
            className={`px-3 py-1.5 rounded-lg text-sm font-mono uppercase tracking-[0.16em] transition-colors ${status === t.key ? "bg-white/10 text-white" : "text-white/50 hover:text-white hover:bg-white/5"}`}
          >
            {t.label} <span className={t.color}>({counts[t.key] ?? 0})</span>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="text-white/40 py-10 text-center"><Loader2 className="h-5 w-5 mx-auto animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="hud-pill rounded-2xl p-10 text-center text-white/40" data-testid="admin-reports-empty">
          {status === "open" ? "Nothing in the queue — the lobby is behaving." : `No ${status} reports.`}
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <ReportRow key={r.id} report={r} onPatch={(payload) => patch.mutate({ id: r.id, ...payload })} pending={patch.isPending} />
          ))}
        </div>
      )}
    </div>
  );
}

function ReportRow({ report, onPatch, pending }) {
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);
  const created = new Date(report.created_at).toLocaleString();
  const cat = CATEGORY_LABEL[report.category] || report.category;
  const isCritical = report.category === "csam_or_minor" || report.category === "non_consent";
  const isOpen = report.status === "open";

  return (
    <div
      data-testid={`admin-report-${report.id}`}
      className={`hud-pill rounded-xl p-4 border ${isCritical ? "border-red-400/50 bg-red-500/[0.04]" : "border-white/10"}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center shrink-0 ${isCritical ? "bg-red-500/20 border border-red-400/40" : "bg-white/5 border border-white/10"}`}>
          <ShieldAlert className={`h-4 w-4 ${isCritical ? "text-red-300" : "text-white/60"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-mono text-[10px] uppercase tracking-[0.16em] px-2 py-0.5 rounded ${isCritical ? "bg-red-500/20 text-red-200" : "bg-white/10 text-white/70"}`}>{cat}</span>
            <span className="font-mono text-[10px] text-white/40">{report.target_type} · <span className="text-white/60">{report.target_id}</span></span>
            <span className="font-mono text-[10px] text-white/40 inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {created}</span>
            {report.status !== "open" && (
              <span className={`font-mono text-[10px] uppercase tracking-[0.16em] ${report.status === "resolved" ? "text-emerald-300" : "text-white/40"}`}>{report.status}</span>
            )}
          </div>
          <div className="mt-1.5 text-[13px] text-white/70">
            Reported by <span className="text-white/90">{report.reporter_name || report.reporter_email}</span>
          </div>
          {report.details && (
            <div className="mt-1.5 text-[13px] text-white/80 bg-black/25 rounded px-2.5 py-1.5 border border-white/5 break-words">
              &quot;{report.details}&quot;
            </div>
          )}
          {report.context_url && (
            <a href={report.context_url} target="_blank" rel="noreferrer" className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-mono text-cyan-300 hover:text-cyan-200">
              <ExternalLink className="h-3 w-3" /> {report.context_url}
            </a>
          )}
          {report.admin_note && (
            <div className="mt-2 text-[12px] text-amber-200/80">
              <span className="font-mono uppercase tracking-[0.16em] text-[9px] text-amber-300/70 mr-1.5">Admin note</span>
              {report.admin_note}
            </div>
          )}
        </div>
      </div>
      {isOpen && (
        <div className="mt-3 pt-3 border-t border-white/[0.06] space-y-2">
          {expanded ? (
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Internal admin note (optional) — what action did you take?"
              rows={2}
              maxLength={2000}
              data-testid={`admin-report-note-${report.id}`}
              className="bg-[hsl(220_18%_12%)] border-white/10 text-white text-[13px]"
            />
          ) : (
            <button onClick={() => setExpanded(true)} className="text-[11px] font-mono uppercase tracking-[0.16em] text-white/40 hover:text-white/70">+ add internal note</button>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <Button
              size="sm"
              disabled={pending}
              onClick={() => onPatch({ status: "resolved", admin_note: note })}
              data-testid={`admin-report-resolve-${report.id}`}
              className="bg-emerald-400 text-black hover:bg-emerald-300"
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Resolved
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={pending}
              onClick={() => onPatch({ status: "rejected", admin_note: note })}
              data-testid={`admin-report-reject-${report.id}`}
              className="border-white/15 text-white/80 hover:text-white"
            >
              <XCircle className="h-4 w-4 mr-1" /> Reject
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
